import React, { useEffect, useState } from "react";
import Filter from "./ClassFilter";
// import RowActions from "./ClassRowAction";
import ClassDetailModal from "./ClassDetailModal";
import ClassRowActionMenu from "./ClassRowActionMenu";
import Pagination from "../../../components/common/Pagination";
import ClassFormModal from "./ClassFormModal";
import ClassImportModal from "./ClassImportModal";
import {
  getAllClasses,
  fetchClassById,
  fetchClassStudents,
} from "../../../api/elearning/classes/classes";
import { exportClassStudentsToExcel } from "./exportClassStudents";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faUsers } from "@fortawesome/free-solid-svg-icons";

import {
  Users,
  BookOpen,
  Building2,
  Clock,
  FolderOpen,
  School,
  Sparkles,
  CircleHelp,
  FilePenLine,
  FileUp,
} from "lucide-react";
import { hasPermission } from "../../../utils/permissionUtils";
import { startClassPageTour } from "./classPageTour";
import { startClassFormModalTour } from "./classFormModalTour";
import { startClassImportModalTour } from "./classImportModalTour";

import {
  deleteClass,
  updateClassStatus,
} from "../../../api/elearning/classes/classes";

import {
  showSuccessToast,
  showErrorToast,
  showConfirmDialog,
} from "../../../components/ConfirmDialog";
import { confirmDeleteClass } from "./confirmClassDelete";

const EMPTY_DASHBOARD_STATS = {
  totalClasses: 0,
  centerClasses: 0,
  emptyClasses: 0,
  totalAssignedStudents: 0,
};

const buildDashboardStatsFromRecords = (
  records = [],
  totalCount = records.length
) => {
  const safeRecords = Array.isArray(records) ? records : [];
  const centerClasses = safeRecords.filter((c) => {
    const isSchoolClass =
      c.class_type === "school" || (!!c.school_id && c.class_type !== "center");
    return !isSchoolClass;
  }).length;
  const emptyClasses = safeRecords.filter(
    (c) => Number(c.student_total_count ?? c.student_count ?? 0) === 0
  ).length;
  const totalAssignedStudents = safeRecords.reduce(
    (sum, c) => sum + Number(c.student_total_count ?? c.student_count ?? 0),
    0
  );

  return {
    totalClasses: Number(totalCount) || safeRecords.length,
    centerClasses,
    emptyClasses,
    totalAssignedStudents,
  };
};

const ClassCardPage = () => {
  const [classes, setClasses] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [showImportExcel, setShowImportExcel] = useState(false);
  const [filters, setFilters] = useState({});
  const [totalRecords, setTotalRecords] = useState(0);
  const [dashboardStats, setDashboardStats] = useState(EMPTY_DASHBOARD_STATS);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [showDetail, setShowDetail] = useState(false);

  // ===== Detail / Edit modal =====
  const [selectedClass, setSelectedClass] = useState(null);
  const [editingClass, setEditingClass] = useState(null);
  const [showEdit, setShowEdit] = useState(false);
  const [queuedModalTour, setQueuedModalTour] = useState(null);
  const [openActionMenuId, setOpenActionMenuId] = useState(null);
  const canQuickTour = hasPermission("elearning.classes", "R");
  const canUpdateClass = hasPermission("elearning.classes", "U");
  const canDeleteClass = hasPermission("elearning.classes", "D");
  const canDownloadStudents = hasPermission("elearning.classes", "R");
  const showActionsColumn = canUpdateClass || canDeleteClass || canDownloadStudents;

  const stickyHead = "sticky z-30 bg-gray-50/95";
  const stickyBody = "sticky z-20 bg-white group-hover:bg-red-50/30";

  const handleQuickTour = () => {
    window.setTimeout(() => startClassPageTour(), 80);
  };

  const openGuide = () => {
    const guideUrl = `${window.location.origin}/guides/elearning-classes-guide.html`;
    window.open(guideUrl, "_blank", "noopener,noreferrer");
  };

  const handleOpenFormTour = () => {
    setQueuedModalTour("form");
    setShowCreate(true);
  };

  const handleOpenImportTour = () => {
    setQueuedModalTour("import");
    setShowImportExcel(true);
  };

  const fetchClasses = async (
    filters = {},
    page = currentPage,
    size = pageSize
  ) => {
    try {
      const query = { ...filters, page, size };
      const data = await getAllClasses(query);
      const records = Array.isArray(data?.records)
        ? data.records
        : Array.isArray(data)
          ? data
          : [];
      const total = Number(data?.total ?? records.length ?? 0);
      setClasses(records);
      setTotalRecords(total);
      setDashboardStats(
        data.summary
          ? {
              totalClasses: Number(data.summary.total_classes ?? total),
              centerClasses: Number(data.summary.center_classes ?? 0),
              emptyClasses: Number(data.summary.empty_classes ?? 0),
              totalAssignedStudents: Number(
                data.summary.total_assigned_students ?? 0
              ),
            }
          : buildDashboardStatsFromRecords(records, total)
      );
    } catch (err) {
      console.error("Lỗi khi tải danh sách bài học:", err);
      setClasses([]);
      setTotalRecords(0);
      setDashboardStats(EMPTY_DASHBOARD_STATS);
    }
  };

  useEffect(() => {
    fetchClasses(filters, currentPage, pageSize);
  }, [filters, currentPage, pageSize]);

  useEffect(() => {
    if (!queuedModalTour) return;
    if (queuedModalTour === "form" && showCreate) {
      window.setTimeout(() => {
        startClassFormModalTour();
        setQueuedModalTour(null);
      }, 220);
    }
    if (queuedModalTour === "import" && showImportExcel) {
      window.setTimeout(() => {
        startClassImportModalTour();
        setQueuedModalTour(null);
      }, 220);
    }
  }, [queuedModalTour, showCreate, showImportExcel]);

  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
    setCurrentPage(1);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handlePageSizeChange = (size) => {
    setPageSize(size);
    setCurrentPage(1);
    fetchClasses(filters, 1, size);
  };

  const handleOpenDetail = (classroom) => {
    setSelectedClass(classroom);
    setShowDetail(true);
  };

  const handleEditClass = async (classroom) => {
    try {
      const detail = await fetchClassById(classroom.id);
      setEditingClass(detail || classroom);
    } catch {
      setEditingClass(classroom);
    }
    setShowEdit(true);
  };

  const resolveClassroom = (classroomOrId) => {
    if (typeof classroomOrId === "object" && classroomOrId) return classroomOrId;
    return (
      classes.find((c) => c.id === classroomOrId) ||
      (selectedClass?.id === classroomOrId ? selectedClass : null) || {
        id: classroomOrId,
      }
    );
  };

  const handleDeleteClass = async (classroomOrId) => {
    const classroom = resolveClassroom(classroomOrId);
    const id = classroom?.id ?? classroomOrId;

    try {
      const confirm = await confirmDeleteClass(classroom);
      if (!confirm.isConfirmed) return;

      const result = await deleteClass(id);
      const deletedStudentCount = Number(result?.deleted_student_count || 0);
      showSuccessToast(
        deletedStudentCount > 0
          ? `Đã xóa lớp học và ${deletedStudentCount} học viên không còn lớp`
          : "Đã xóa lớp học"
      );
      setShowDetail(false);
      setOpenActionMenuId(null);
      fetchClasses(filters, currentPage, pageSize);
    } catch (error) {
      console.error("Lỗi khi xóa lớp học:", error);
      showErrorToast(error?.message || "Không thể xóa lớp học");
    }
  };

  const handleToggleClassStatus = async (classroomOrId, nextActiveArg) => {
    const classroom = resolveClassroom(classroomOrId);
    const id = classroom?.id ?? classroomOrId;
    const isActive =
      classroom?.is_active === true || classroom?.status === "active";
    const nextActive =
      typeof nextActiveArg === "boolean" ? nextActiveArg : !isActive;
    const confirm = await showConfirmDialog({
      title: isActive ? "Đóng lớp học" : "Mở lớp học",
      text: isActive
        ? `Bạn chắc chắn muốn đóng lớp học "${classroom?.name}"?`
        : `Bạn chắc chắn muốn mở lại lớp học "${classroom?.name}"?`,
      confirmText: isActive ? "Đóng" : "Mở",
      cancelText: "Huỷ",
    });
    if (!confirm.isConfirmed) return;

    try {
      await updateClassStatus(id, nextActive);
      showSuccessToast("Đã cập nhật trạng thái lớp học");
      setShowDetail(false);
      fetchClasses(filters, currentPage, pageSize);
    } catch (error) {
      console.error("Lỗi khi cập nhật trạng thái:", error);
      showErrorToast("Không thể cập nhật trạng thái lớp học");
    }
  };

  const handleDownloadClassStudents = async (classroom) => {
    try {
      const data = await fetchClassStudents(classroom.id);
      const list = Array.isArray(data) ? data : data?.records || data?.students || [];
      const result = exportClassStudentsToExcel(classroom, list);
      if (!result.ok) {
        showErrorToast(result.message);
        return;
      }
      showSuccessToast("Đã tải danh sách học viên");
    } catch (error) {
      console.error("Lỗi tải DS học viên:", error);
      showErrorToast("Không thể tải danh sách học viên");
    }
  };

  return (
    <div className="p-4">
      {/* HEADER */}
      <div
        className="flex items-start sm:items-center justify-between gap-4 mb-8"
        data-tour="classes-header"
      >
        <div className="flex items-center gap-4">
          {/* Icon Wrapper với Shadow Red */}
          <div className="bg-red-700 p-2.5 rounded-2xl text-white shadow-lg shadow-red-200">
            <FontAwesomeIcon
              icon={faUsers}
              className="text-xl w-6 h-6 flex items-center justify-center"
            />
          </div>

          <div>
            {/* Title với style font-black và uppercase đồng bộ */}
            <h1 className="text-xl font-black text-gray-800 tracking-tight uppercase">
              Quản lý <span className="text-red-700">lớp học</span>
            </h1>

            {/* Subtitle với hiệu ứng pulse và font tracking-widest */}
            <div className="flex items-center gap-2 mt-0.5">
              <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse"></span>
              <p className="text-[11px] text-gray-500 font-bold uppercase tracking-wider text-nowrap">
                <span className="text-red-600">{classes.length}</span> lớp học
                đang hiển thị
              </p>
            </div>
          </div>
        </div>
        <div
          className="flex items-center gap-2 shrink-0"
          data-tour="classes-header-guides"
        >
          {canQuickTour ? (
            <HeaderIconButton
              icon={Sparkles}
              label="Hướng dẫn nhanh"
              onClick={handleQuickTour}
            />
          ) : null}
          <HeaderIconButton
            icon={CircleHelp}
            label="Hướng dẫn sử dụng"
            onClick={openGuide}
          />
          {canQuickTour ? (
            <>
              <HeaderIconButton
                icon={FilePenLine}
                label="Tour form"
                onClick={handleOpenFormTour}
              />
              <HeaderIconButton
                icon={FileUp}
                label="Tour import"
                onClick={handleOpenImportTour}
              />
            </>
          ) : null}
        </div>
      </div>

      <div
        className="grid grid-cols-2 xl:grid-cols-4 gap-4 my-6"
        data-tour="classes-mini-dashboard"
      >
        <MiniDashboardCard
          label="Tổng lớp học"
          value={dashboardStats.totalClasses}
          icon={BookOpen}
          iconClass="text-red-600"
          bgClass="bg-red-50"
        />
        <MiniDashboardCard
          label="Lớp tại trung tâm"
          value={dashboardStats.centerClasses}
          icon={Building2}
          iconClass="text-blue-600"
          bgClass="bg-blue-50"
        />
        <MiniDashboardCard
          label="Lớp chưa có học viên"
          value={dashboardStats.emptyClasses}
          icon={Clock}
          iconClass="text-amber-600"
          bgClass="bg-amber-50"
        />
        <MiniDashboardCard
          label="Tổng số học viên"
          value={dashboardStats.totalAssignedStudents}
          icon={Users}
          iconClass="text-blue-600"
          bgClass="bg-blue-50"
        />
      </div>

      <div data-tour="classes-filter">
        <Filter
          onNewClick={() => setShowCreate(true)}
          onImportExcelClick={() => setShowImportExcel(true)}
          onFilterChange={handleFilterChange} // ✅ Thêm dòng này
        />
      </div>

      {/* TABLE */}
      <div
        className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden"
        data-tour="classes-table"
      >
        <div className="overflow-x-auto">
          <table className="w-full table-auto border-collapse">
            {/* HEADER */}
            <thead>
              <tr className="bg-gray-50/50 border-b border-gray-100 text-left">
                <th
                  className={`p-4 w-[50px] min-w-[50px] text-center text-xs font-bold text-gray-400 uppercase tracking-wider ${stickyHead} left-0`}
                >
                  #
                </th>
                <th
                  className={`p-4 min-w-[140px] text-xs font-bold text-gray-400 uppercase tracking-wider ${stickyHead} left-[50px] border-r-2 border-gray-100`}
                >
                  Lớp học
                </th>
                <th className="p-4 min-w-[180px] text-xs font-bold text-gray-400 uppercase tracking-wider">
                  Khóa học
                </th>
                <th className="p-4 min-w-[150px] text-xs font-bold text-gray-400 uppercase tracking-wider">
                  Đơn vị
                </th>
                <th className="p-4 min-w-[180px] text-xs font-bold text-gray-400 uppercase tracking-wider">
                  Giáo viên
                </th>
                <th className="p-4 min-w-[100px] text-center text-xs font-bold text-gray-400 uppercase tracking-wider">
                  Sĩ số
                </th>
                <th className="p-4 min-w-[140px] text-center text-xs font-bold text-gray-400 uppercase tracking-wider">
                  Trạng thái
                </th>
                {showActionsColumn ? (
                  <th
                    className={`p-4 w-[72px] min-w-[72px] text-center text-xs font-bold text-gray-400 uppercase tracking-wider ${stickyHead} right-0 border-l-2 border-gray-100`}
                  >
                    Tác vụ
                  </th>
                ) : null}
              </tr>
            </thead>

            {/* BODY */}
            <tbody className="divide-y divide-gray-50">
              {classes.map((c, index) => (
                <tr
                  key={c.id}
                  onClick={() => handleOpenDetail(c)}
                  className="hover:bg-red-50/30 transition-colors group cursor-pointer"
                >
                  {/* 1. STT */}
                  <td
                    className={`p-4 text-sm text-gray-400 text-center font-medium italic ${stickyBody} left-0`}
                  >
                    {String((currentPage - 1) * pageSize + index + 1).padStart(
                      2,
                      "0"
                    )}
                  </td>

                  {/* 2. MÃ & TÊN LỚP */}
                  <td
                    className={`p-4 ${stickyBody} left-[50px] border-r-2 border-gray-100`}
                  >
                    <div className="flex flex-col">
                      <span className="text-[10px] font-bold text-red-500 font-mono tracking-tight">
                        #{c.code || "N/A"}
                      </span>
                      <span className="font-bold text-gray-900 group-hover:text-red-600 transition-colors text-sm">
                        {c.name}
                      </span>
                    </div>
                  </td>

                  {/* 3. KHÓA HỌC */}
                  <td className="p-4">
                    <div className="flex items-center gap-2">
                      <div className="p-1.5 bg-blue-50 rounded-lg">
                        <BookOpen size={14} className="text-blue-600" />
                      </div>
                      <span className="text-sm text-gray-600 font-medium">
                        {c.course_name || "_"}
                      </span>
                    </div>
                  </td>

                  {/* 4. ĐƠN VỊ */}
                  <td className="p-4">
                    <div className="space-y-1.5">
                      <div className="flex items-center gap-2 text-xs font-bold text-gray-700">
                        <Building2 size={12} className="text-gray-400 shrink-0" />
                        <span>{c.branch_name || "_"}</span>
                      </div>
                      <div className="flex items-center gap-2 text-[11px] text-blue-700 font-medium">
                        <School size={12} className="text-blue-500 shrink-0" />
                        <span>
                          {c.class_type === "school"
                            ? c.school_name || "Chưa chọn trường học"
                            : "Trung tâm"}
                        </span>
                      </div>
                    </div>
                  </td>

                  {/* 5. GIÁO VIÊN */}
                  <td className="p-4">
                    <div className="flex items-start gap-3">
                      <div
                        className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-xs shrink-0 border border-white shadow-sm transition-colors ${
                          c.teacher_name
                            ? "bg-blue-100 text-blue-600"
                            : "bg-gray-100 text-gray-400"
                        }`}
                      >
                        {c.teacher_name?.charAt(0) || "?"}
                      </div>

                      <div className="space-y-1">
                        <div
                          className={`text-sm transition-colors ${
                            c.teacher_name
                              ? "font-bold text-gray-800"
                              : "font-medium text-gray-400 italic"
                          }`}
                        >
                          {c.teacher_name || "Chưa có GV"}
                        </div>
                        <div className="flex items-center gap-1.5 text-[11px] text-orange-600 font-medium">
                          <div className="w-1.5 h-1.5 rounded-full bg-orange-400" />
                          {`Phòng: ${c.room_name || "Chưa xếp phòng"}`}
                        </div>
                      </div>
                    </div>
                  </td>

                  {/* 6. SĨ SỐ */}
                  <td className="p-4 text-center">
                    <div
                      className="inline-flex items-center gap-1 px-2.5 py-1 bg-gray-50 rounded-lg border border-gray-100 font-bold text-gray-700 text-sm"
                      title="Sĩ số hoạt động / Tổng sĩ số"
                    >
                      <Users size={12} className="text-gray-400" />
                      {`${c.student_active_count ?? c.student_count ?? 0}/${c.student_total_count ?? c.student_count ?? 0}`}
                    </div>
                  </td>

                  {/* 7. TRẠNG THÁI */}
                  <td className="p-4 text-center">
                    <span
                      className={`inline-flex items-center px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wide border
                ${
                  c.is_active
                    ? "bg-green-50 text-green-700 border-green-100"
                    : "bg-gray-50 text-gray-500 border-gray-100"
                }`}
                    >
                      <span
                        className={`w-1.5 h-1.5 rounded-full mr-1.5 ${
                          c.is_active
                            ? "bg-green-500 animate-pulse"
                            : "bg-gray-400"
                        }`}
                      />
                      {c.is_active ? "Đang mở" : "Đã đóng"}
                    </span>
                  </td>
                  {showActionsColumn ? (
                    <td
                      className={`p-4 text-center align-middle sticky right-0 border-l-2 border-gray-100 bg-white group-hover:bg-red-50/30 ${
                        openActionMenuId === c.id ? "z-50" : "z-20"
                      }`}
                    >
                      <ClassRowActionMenu
                        classroom={c}
                        isOpen={openActionMenuId === c.id}
                        onOpenChange={(open) =>
                          setOpenActionMenuId(open ? c.id : null)
                        }
                        onEdit={handleEditClass}
                        onDelete={handleDeleteClass}
                        onDownload={handleDownloadClassStudents}
                        onToggleStatus={handleToggleClassStatus}
                      />
                    </td>
                  ) : null}
                </tr>
              ))}

              {/* EMPTY STATE */}
              {classes.length === 0 && (
                <tr>
                  <td
                    colSpan={showActionsColumn ? 8 : 7}
                    className="p-20 text-center"
                  >
                    <div className="flex flex-col items-center">
                      <div className="w-16 h-16 bg-gray-50 rounded-2xl flex items-center justify-center mb-4">
                        <FolderOpen size={32} className="text-gray-200" />
                      </div>
                      <p className="text-gray-400 font-medium text-sm">
                        Hiện chưa có dữ liệu lớp học nào.
                      </p>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* PAGINATION SECTION */}
      {totalRecords > 0 && (
        <Pagination
          totalRecords={totalRecords}
          currentPage={currentPage}
          pageSize={pageSize}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
        />
      )}

      {showCreate && (
        <ClassFormModal
          isOpen={showCreate}
          mode="create"
          onClose={() => {
            setShowCreate(false);
            setQueuedModalTour(null);
          }}
          onSaved={() => {
            fetchClasses(filters, currentPage, pageSize);
            setShowCreate(false);
            setQueuedModalTour(null);
          }}
        />
      )}

      <ClassImportModal
        isOpen={showImportExcel}
        onClose={() => {
          setShowImportExcel(false);
          setQueuedModalTour(null);
        }}
        onSuccess={() => fetchClasses(filters, currentPage, pageSize)}
      />

      {showEdit && (
        <ClassFormModal
          isOpen={showEdit}
          mode="edit"
          editingClass={editingClass}
          onClose={() => {
            setShowEdit(false);
            setEditingClass(null);
          }}
          onSaved={() => {
            fetchClasses(filters, currentPage, pageSize);
            setShowEdit(false);
          }}
        />
      )}

      {showDetail && (
        <ClassDetailModal
          classroom={selectedClass}
          onClose={() => setShowDetail(false)}
          onEdit={async (c) => {
            setShowDetail(false);
            await handleEditClass(c);
          }}
          onDelete={handleDeleteClass}
          onToggleStatus={handleToggleClassStatus}
        />
      )}
    </div>
  );
};

function HeaderIconButton({ icon: Icon, label, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={label}
      aria-label={label}
      className="group relative inline-flex h-11 w-11 items-center justify-center rounded-2xl border border-red-200 bg-white text-red-700 shadow-sm transition-all hover:bg-red-50 hover:border-red-300"
    >
      <Icon className="h-5 w-5" strokeWidth={2.2} />
      <span className="pointer-events-none absolute bottom-full right-0 mb-2 rounded-lg border border-red-100 bg-white px-2.5 py-1 text-[11px] font-bold text-red-700 whitespace-nowrap opacity-0 translate-y-1 shadow-md transition-all group-hover:opacity-100 group-hover:translate-y-0 group-focus-visible:opacity-100 group-focus-visible:translate-y-0">
        {label}
      </span>
    </button>
  );
}

function MiniDashboardCard({ label, value, icon: Icon, iconClass, bgClass }) {
  const displayValue = new Intl.NumberFormat("vi-VN").format(
    Number(value) || 0
  );

  return (
    <div className="bg-white rounded-2xl border border-gray-100 p-5 shadow-sm transition-all hover:shadow-md">
      <div className="flex items-center gap-3">
        <div className={`p-2.5 ${bgClass} rounded-xl`}>
          <Icon size={20} className={iconClass} />
        </div>
        <div>
          <p className="text-[11px] font-bold text-gray-400 uppercase tracking-wider">
            {label}
          </p>
          <p className="text-xl font-black text-gray-800">{displayValue}</p>
        </div>
      </div>
    </div>
  );
}

export default ClassCardPage;
